import { Link, useParams, Navigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { SERVICES, EQUIPMENT, SITE } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { CTABand, PageHero } from '../components/chrome';
import { RFQForm } from '../components/RFQForm';

export default function ServicePage() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);
  if (!svc) return <Navigate to="/services" replace />;

  const idx = SERVICES.indexOf(svc);
  const crumbs = [{ label: 'Home', href: '/' }, { label: 'Services', href: '/services' }, { label: svc.name }];
  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: svc.name,
    description: svc.meta.desc,
    provider: { '@type': 'Organization', name: SITE.name, url: SITE.url },
    areaServed: 'United States (nationwide)',
  };

  const relatedEquipment = EQUIPMENT.filter((_, i) => (idx % 3 === i % 3)).slice(0, 4);

  return (
    <div data-testid={`page-service-${svc.slug}`}>
      <Seo title={svc.meta.title} description={svc.meta.desc} path={`/services/${svc.slug}`} ld={[serviceLd]} breadcrumbs={crumbs} />
      <PageHero
        eyebrow={`Services · ${String(idx + 1).padStart(2, '0')} / ${String(SERVICES.length).padStart(2, '0')}`}
        lines={svc.name}
        sub={svc.tagline}
        breadcrumbs={crumbs}
        image={svc.image}
        imageAlt={svc.alt}
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <Reveal>
              <h2 className="display text-2xl sm:text-3xl text-ink">What this engagement includes</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">{svc.intro}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <ul className="mt-8 border-t border-slate-200">
                {svc.includes.map((it, i) => (
                  <li key={it} className="flex items-start gap-3 py-3.5 border-b border-slate-200 text-slate-700">
                    <span className="font-mono text-xs text-signal mt-1 tabular">{String(i + 1).padStart(2, '0')}</span>
                    {it}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.05}><SpotlightImage src={svc.image} alt={svc.alt} ratio="aspect-[4/3]" /></Reveal>
            <Reveal delay={0.1}>
              <div className="bg-ink text-white p-7 mt-8">
                <p className="eyebrow text-signal">Discuss a requirement</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Every service engagement starts with a short conversation about the mission, the instruments involved, and the timeline.
                </p>
                <a href={`mailto:${SITE.email}?subject=${encodeURIComponent('Service inquiry: ' + svc.name)}`} className="btn-light w-full mt-5" data-testid="service-cta-button">Discuss a Requirement</a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50 hairline-t">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">RFQ</p>
            <h2 className="display text-3xl sm:text-4xl text-ink mb-8">Request this service</h2>
          </Reveal>
          <RFQForm defaultNeed={svc.name} />
        </div>
      </section>

      <section className="section-pad hairline-t">
        <div className="container-x">
          <h2 className="eyebrow text-signal mb-6">Equipment this service supports</h2>
          <div className="flex flex-wrap gap-2">
            {relatedEquipment.map((e) => (
              <Link key={e.slug} to={`/equipment/${e.slug}`} className="border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:text-signal hover:border-signal transition-colors" data-testid={`service-related-equipment-${e.slug}`}>
                {e.name} <ArrowRight size={12} className="inline ml-1" />
              </Link>
            ))}
            <Link to="/equipment" className="border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-signal" data-testid="service-all-equipment-link">All 18 categories</Link>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
